import type { ViewRecord } from "@/shared/domain/views";

import { staticFilterFieldsFor } from "../Filters/catalog";
import type { UseResourceViewOptions } from "../useResourceView";
import { definitionToViewState } from "../viewSerialization";

import { effectiveLayout, overlayDraft } from "./layoutDrafts";
import type { LayoutDrafts } from "./useLayoutDrafts";

/**
 * Merge a View into the options a list hands to `useResourceView`, so the
 * table starts out showing the View instead of starting empty and being
 * corrected a moment later. `useResourceView` reads these options once, on
 * mount, so this is the one chance to start the table in the right state.
 *
 * Returns `null` when the View cannot be restored synchronously, and the
 * ordinary resolve-then-apply path should own the navigation instead:
 *
 * - Written under a scheme this client does not read. `useApplyView` raises
 *   the same failure on the apply path, which is where the customer is told.
 * - A RoboQL View. Restoring one means turning its query into filters, and
 *   only the server can do that.
 *
 * A View with no filters seeds like any other: it was saved for its columns
 * and sort, and the table starts out showing those.
 *
 * With `drafts`, the layout is what applying the View would produce: the
 * View's display with its gaps filled from the built-in layout, and the
 * customer's draft for the View laid over that. Without them, a dimension the
 * View recorded no preference for defers to the list's own options rather
 * than blanking it: `null` means "unstated", not "none".
 */
export function seedResourceViewOptions<TData>(
  record: ViewRecord,
  options: UseResourceViewOptions<TData>,
  drafts?: LayoutDrafts,
): UseResourceViewOptions<TData> | null {
  let restored;
  try {
    restored = definitionToViewState(
      record.definition,
      staticFilterFieldsFor(record.target),
    );
  } catch {
    return null;
  }

  const { filters, sort, pageSize, visibleColumns } = restored;
  if (filters.type === "roboql") {
    return null;
  }

  if (drafts !== undefined) {
    const layout = overlayDraft(
      effectiveLayout(record.definition.display, drafts.baseline),
      drafts.draftFor(record.view_id),
    );
    return {
      ...options,
      initialActiveFilters: filters,
      initialSort: layout.sort ?? undefined,
      preferences: {
        columns: layout.visibleColumns,
        rowsPerPage: layout.pageSize,
        sort: layout.sort,
      },
    };
  }

  return {
    ...options,
    initialActiveFilters: filters,
    initialSort: sort ?? options.initialSort,
    preferences: {
      columns: visibleColumns ?? options.preferences?.columns,
      rowsPerPage: pageSize ?? options.preferences?.rowsPerPage,
    },
  };
}
